<script setup>
import { computed } from 'vue';
import { useChatStore } from '../stores/chat.js';
import { Plus, MessageSquare, Trash2, PanelLeftClose, PanelLeft } from 'lucide-vue-next';

const chatStore = useChatStore();

const conversations = computed(() => chatStore.sortedConversations);
const currentId = computed(() => chatStore.currentConversationId);
const isOpen = computed(() => chatStore.sidebarOpen);
const hasConversations = computed(() => conversations.value.length > 0);

function handleNewChat() {
  chatStore.createConversation();
}

function handleSelectConversation(id) {
  chatStore.selectConversation(id);
}

function handleDeleteConversation(e, id) {
  e.stopPropagation();
  if (confirm('Delete this conversation?')) {
    chatStore.deleteConversation(id);
  }
}

function handleToggleSidebar() {
  chatStore.toggleSidebar();
}

function formatDate(timestamp) {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;

  if (diff < 24 * 60 * 60 * 1000 && date.getDate() === now.getDate()) {
    return 'Today';
  }

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.getDate() === yesterday.getDate()) {
    return 'Yesterday';
  }

  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const groupedConversations = computed(() => {
  const groups = {};
  conversations.value.forEach((conv) => {
    const dateKey = formatDate(conv.updatedAt);
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(conv);
  });
  return groups;
});
</script>

<template>
  <aside
    class="flex flex-col bg-sidebar h-full transition-sidebar overflow-hidden shrink-0 border-r border-border"
    :class="isOpen ? 'w-[260px]' : 'w-0'"
  >
    <!-- Header with New Chat button -->
    <div class="flex items-center gap-2 px-3 py-3">
      <button
        @click="handleNewChat"
        class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-text-primary hover:bg-sidebar-hover transition-colors flex-1"
      >
        <Plus :size="18" class="text-text-secondary" />
        <span class="text-sm font-medium">New chat</span>
      </button>

      <button
        @click="handleToggleSidebar"
        class="p-2.5 rounded-lg text-text-secondary hover:bg-sidebar-hover hover:text-text-primary transition-colors shrink-0"
        title="Close sidebar"
      >
        <PanelLeftClose :size="18" />
      </button>
    </div>

    <!-- Conversations list -->
    <div class="flex-1 overflow-y-auto px-3 pt-2 pb-4">
      <template v-if="hasConversations">
        <div v-for="(convs, dateKey) in groupedConversations" :key="dateKey" class="mb-6">
          <div class="px-3 py-2 text-xs font-medium text-text-muted tracking-wide">
            {{ dateKey }}
          </div>
          <div class="space-y-0.5">
            <div
              v-for="conv in convs"
              :key="conv.id"
              @click="handleSelectConversation(conv.id)"
              class="group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors"
              :class="currentId === conv.id ? 'bg-sidebar-hover' : 'hover:bg-sidebar-hover/70'"
            >
              <MessageSquare :size="16" class="text-text-muted shrink-0" />
              <span class="flex-1 text-sm text-text-primary truncate">
                {{ conv.title }}
              </span>
              <button
                @click="(e) => handleDeleteConversation(e, conv.id)"
                class="p-1.5 rounded-md opacity-0 group-hover:opacity-100 hover:bg-main-bg transition-all shrink-0"
                title="Delete conversation"
              >
                <Trash2 :size="14" class="text-text-muted hover:text-red-400 transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </template>

      <div v-else class="flex flex-col items-center justify-center py-16 px-4 text-center">
        <MessageSquare :size="28" class="text-text-muted/40 mb-3" />
        <p class="text-text-muted text-sm leading-relaxed">
          No conversations yet.<br />
          Start a new chat!
        </p>
      </div>
    </div>
  </aside>

  <!-- Sidebar toggle button (when closed) -->
  <button
    v-if="!isOpen"
    @click="handleToggleSidebar"
    class="absolute top-3 left-3 z-50 p-2.5 rounded-lg bg-main-bg border border-border hover:bg-sidebar-hover text-text-secondary hover:text-text-primary transition-colors"
    title="Open sidebar"
  >
    <PanelLeft :size="18" />
  </button>
</template>
<style lang="scss" scoped>
@tailwind base;
@tailwind components;
@tailwind utilities;
@import './../main.css';
</style>
